import React from 'react';
import ReactDOM from 'react-dom';
import i18n from 'i18n-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { SmartAssistMarkdown } from './SmartAssistMarkdown';
import { SmartAssistStructuredResponse } from './SmartAssistStructuredResponse';
import { SmartAssistChatSidebar } from './SmartAssistChatSidebar';
import { SmartAssistSuggestions } from './SmartAssistSuggestions';
import { linkOpensNewTab, ragLinkLabel } from './smartAssistUtils';
import type { ConversationEntry, Message, MessageQuota, RagLink, TrainingProgress } from './types';

export interface SmartAssistChatProps {
  chatOpen: boolean;
  greeting: string;
  destinationLabel: string;
  messages: Message[];
  historyLoading: boolean;
  messageQuota: MessageQuota | null;
  trainingProgress: TrainingProgress | null;
  isStreaming: boolean;
  isQuotaBlocked: boolean;
  deletingConvId: string | null;
  query: string;
  sidebarRows: ConversationEntry[];
  activeSidebarThreadId: string | null;
  ragBarItems: RagLink[];
  quickSuggestionsChat: string[];
  showChatSuggestionSwitch: boolean;
  lastMessageRowRef: React.RefObject<HTMLDivElement | null>;
  chatInputRef: React.RefObject<HTMLInputElement | null>;
  onClose: () => void;
  onNewChat: () => void;
  onChatSubmit(event: React.FormEvent): void;
  onSuggestionClick(suggestion: string): void;
  onSavedConversationOpen(entry: ConversationEntry): void;
  onDeleteConversation(conversationId: string): Promise<void>;
  onRotateChatSuggestions: () => void;
  onQueryChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

const formatQuotaReset = (resetsAt: string | undefined): string => {
  if (!resetsAt) return '';
  const date = new Date(resetsAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

export function SmartAssistChat({
  chatOpen,
  greeting,
  destinationLabel,
  messages,
  historyLoading,
  messageQuota,
  trainingProgress,
  isStreaming,
  isQuotaBlocked,
  deletingConvId,
  query,
  sidebarRows,
  activeSidebarThreadId,
  ragBarItems,
  quickSuggestionsChat,
  showChatSuggestionSwitch,
  lastMessageRowRef,
  chatInputRef,
  onClose,
  onNewChat,
  onChatSubmit,
  onSuggestionClick,
  onSavedConversationOpen,
  onDeleteConversation,
  onRotateChatSuggestions,
  onQueryChange,
}: SmartAssistChatProps): React.ReactPortal | null {
  if (!chatOpen) return null;

  const remaining = Number(messageQuota?.remaining ?? messageQuota?.remaining_messages ?? 0);
  const isGraduated = trainingProgress?.foundation_complete === true;
  const showQuotaStatus = messageQuota != null && (!isGraduated || remaining < 5);
  const total = Number(messageQuota?.total ?? messageQuota?.message_limit ?? 0);
  const quotaPercent = total > 0 ? Math.max(0, Math.min(100, (remaining / total) * 100)) : null;
  const resetLabel = formatQuotaReset(messageQuota?.resets_at);
  const incompleteChapters =
    trainingProgress?.available === false || !Array.isArray(trainingProgress?.first_incomplete_chapters)
      ? []
      : trainingProgress.first_incomplete_chapters.filter((ch) => ch?.url && ch?.title);

  const isEmpty = messages.length === 0 && !historyLoading;

  return ReactDOM.createPortal(
    <div className="smartAssist__overlay">
      <div className="smartAssist__overlay-backdrop" onClick={onClose} />
      <div className="smartAssist__chat-wrapper">

        <SmartAssistChatSidebar
          sidebarRows={sidebarRows}
          activeSidebarThreadId={activeSidebarThreadId}
          deletingConvId={deletingConvId}
          onOpen={onSavedConversationOpen}
          onDelete={(id) => {
            void onDeleteConversation(id);
          }}
        />

        <div className="smartAssist__chat">
          <div className="smartAssist__chat-header">
            <div className="smartAssist__chat-greeting">
              <div className="smartAssist__chat-greeting-name">
                <FontAwesomeIcon icon={['fas', 'sparkles']} />
                {greeting}
              </div>
              <div className="smartAssist__chat-greeting-subtitle">
                {i18n.translate('smartAssist.chatSubtitle', { destinationLabel })}
              </div>
            </div>
            <div className="smartAssist__chat-header-actions">
              {showChatSuggestionSwitch && (
                <button
                  type="button"
                  className={clsx(
                    'smartAssist__quick-suggestions-switch',
                    'smartAssist__quick-suggestions-switch--chat-header',
                    'smartAssist__quick-suggestions-switch--next',
                  )}
                  onClick={onRotateChatSuggestions}
                  aria-label={i18n.translate('smartAssist.showNextSuggestionsAria') as string}
                >
                  <FontAwesomeIcon icon={['fas', 'exchange-alt']} />
                </button>
              )}
              <button
                type="button"
                className={clsx(
                  'smartAssist__quick-suggestions-switch',
                  'smartAssist__quick-suggestions-switch--chat-header',
                )}
                onClick={onNewChat}
                aria-label={i18n.translate('smartAssist.startNewConversationAria') as string}
              >
                <FontAwesomeIcon icon={['fas', 'plus']} />
              </button>
              <button type="button" className="smartAssist__chat-close" onClick={onClose}>
                <FontAwesomeIcon icon={['fas', 'xmark']} />
              </button>
            </div>
          </div>

          {ragBarItems.length > 0 && (
            <div className="smartAssist__chat-matched-bar">
              <div className="smartAssist__programs-label">{i18n.translate('smartAssist.sourcesFromApp')}</div>
              <div className="smartAssist__programs">
                {ragBarItems.map((link, idx) => {
                  const href = link.cms.url;
                  const newTab = linkOpensNewTab(href);
                  return (
                    <a
                      key={`${link.source_id}-${link.kind}-${idx}`}
                      href={href}
                      className={clsx('smartAssist__program-card', 'smartAssist__program-card--rag')}
                      onClick={onClose}
                      target={newTab ? '_blank' : undefined}
                      rel={newTab ? 'noopener noreferrer' : undefined}
                    >
                      {link.cms.image_url ? (
                        <div
                          className="smartAssist__program-card-thumb"
                          style={{ backgroundImage: `url(${link.cms.image_url})` }}
                        />
                      ) : (
                        <div
                          className={clsx(
                            'smartAssist__program-card-visual',
                            'smartAssist__program-card-visual--rag',
                          )}
                        >
                          <FontAwesomeIcon icon={['fas', 'book-open']} />
                        </div>
                      )}
                      <div className="smartAssist__program-card-title">{ragLinkLabel(link)}</div>
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          <div className="smartAssist__chat-messages">
            {historyLoading && messages.length === 0 && (
              <div className="smartAssist__history-loading">
                <span
                  className="smartAssist__thinking-dots"
                  aria-label={i18n.translate('smartAssist.loadingConversation') as string}
                >
                  <span className="smartAssist__thinking-dot" />
                  <span className="smartAssist__thinking-dot" />
                  <span className="smartAssist__thinking-dot" />
                </span>
              </div>
            )}

            {isEmpty && (
              <div className="smartAssist__chat-welcome">
                <div className="smartAssist__chat-welcome-icon">
                  <FontAwesomeIcon icon={['fas', 'sparkles']} />
                </div>
                <p className="smartAssist__chat-welcome-prompt">
                  {i18n.translate('smartAssist.welcomePrompt', { destinationLabel })}
                </p>
                <SmartAssistSuggestions suggestions={quickSuggestionsChat} onSelect={onSuggestionClick} welcome />
              </div>
            )}

            {messages.map((msg, idx) => (
              <div
                key={msg.id}
                ref={idx === messages.length - 1 ? lastMessageRowRef : null}
                className={clsx('smartAssist__msg', `smartAssist__msg--${msg.role}`)}
              >
                {msg.role === 'assistant' && (
                  <div className="smartAssist__msg-avatar">
                    <FontAwesomeIcon icon={['fas', 'sparkles']} />
                  </div>
                )}
                <div className="smartAssist__msg-column">
                  <div
                    className={clsx(
                      'smartAssist__msg-bubble',
                      msg.streaming && 'smartAssist__msg-bubble--streaming',
                    )}
                  >
                    {msg.role === 'assistant' && msg.streaming && !msg.text ? (
                      <span
                        className="smartAssist__thinking-dots"
                        aria-label={i18n.translate('smartAssist.loadingResponse') as string}
                      >
                        <span className="smartAssist__thinking-dot" />
                        <span className="smartAssist__thinking-dot" />
                        <span className="smartAssist__thinking-dot" />
                      </span>
                    ) : msg.role === 'assistant' ? (
                      <div className="smartAssist__msg-markdown">
                        <SmartAssistStructuredResponse text={msg.text} streaming={msg.streaming ?? false} />
                      </div>
                    ) : (
                      <div className={clsx('smartAssist__msg-markdown', 'smartAssist__msg-markdown--user')}>
                        <SmartAssistMarkdown>{msg.text}</SmartAssistMarkdown>
                      </div>
                    )}
                  </div>
                  {msg.role === 'assistant' && !msg.streaming && (msg.links?.length ?? 0) > 0 && (
                    <ul className="smartAssist__msg-sources">
                      {msg.links
                        .filter((l) => l.cms?.url)
                        .map((link, linkIdx) => {
                          const href = link.cms.url;
                          const newTab = linkOpensNewTab(href);
                          return (
                            <li key={`${link.source_id}-${linkIdx}`}>
                              <a
                                href={href}
                                target={newTab ? '_blank' : undefined}
                                rel={newTab ? 'noopener noreferrer' : undefined}
                              >
                                {ragLinkLabel(link)}
                              </a>
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="smartAssist__chat-bottom">
            {showQuotaStatus && (
              <div
                className={clsx(
                  'smartAssist__quota-status',
                  isQuotaBlocked && 'smartAssist__quota-status--blocked',
                )}
              >
                <div className="smartAssist__quota-primary">
                  <FontAwesomeIcon icon={isQuotaBlocked ? ['fas', 'exclamation-circle'] : ['fas', 'info-circle']} />
                  <span>
                    {isQuotaBlocked
                      ? i18n.translate('smartAssist.messageLimitReached')
                      : i18n.translate(
                          remaining === 1 ? 'smartAssist.messageRemaining' : 'smartAssist.messagesRemaining',
                          { remaining },
                        )}
                  </span>
                </div>
                {quotaPercent != null && (
                  <div
                    className="smartAssist__quota-bar"
                    role="progressbar"
                    aria-valuenow={remaining}
                    aria-valuemax={total}
                    aria-label={i18n.translate('smartAssist.quotaBarAria', { remaining, total }) as string}
                  >
                    <div className="smartAssist__quota-bar-fill" style={{ width: `${quotaPercent}%` }} />
                  </div>
                )}
                {resetLabel && (
                  <div className="smartAssist__quota-reset">
                    {i18n.translate('smartAssist.resetsAt', { resetLabel })}
                  </div>
                )}
                {incompleteChapters.length > 0 && !isGraduated && (
                  <div className="smartAssist__quota-chapters">
                    <span>{i18n.translate('smartAssist.quotaChaptersPrompt')}</span>
                    {incompleteChapters.map((ch, idx) => (
                      <span key={ch.id ?? ch.url}>
                        <a href={ch.url}>{ch.title}</a>
                        {idx < incompleteChapters.length - 1 && ','}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <form onSubmit={onChatSubmit} className="smartAssist__chat-input-bar">
              <input
                ref={chatInputRef}
                type="text"
                className="smartAssist__chat-input"
                placeholder={i18n.translate('smartAssist.chatPlaceholder') as string}
                value={query}
                onChange={onQueryChange}
              />
              <button type="submit" className="smartAssist__chat-send" disabled={isStreaming || isQuotaBlocked}>
                <FontAwesomeIcon icon={['fas', 'paper-plane']} />
              </button>
            </form>

            {!isEmpty && (
              <SmartAssistSuggestions suggestions={quickSuggestionsChat} onSelect={onSuggestionClick} />
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
